import { z } from 'zod';

export const ExternalManagedPaymentServiceTypeSchema = z.string();

export const ExternalManagedErrorResStatusSchema = z.enum([
  'INVALID_SERVICE_TYPE',
  'EXTERNAL_SERVICE_NOT_FOUND',
  'EXTERNAL_SERVICE_HAS_NO_ACTIVE_PAYMENT_METHODS',
  'ORDER_NOT_FOUND',
  'ORDER_NOT_ACTIVE',
  'ORDER_INSTALLMENT_TYPE_NOT_EXTERNAL_MANAGED',
  'INVALID_PAYMENT_AMOUNT',
  'PAYMENT_REFERENCE_NUMBER_ALREADY_EXISTS',
  'INVALID_PAYMENT_PROOF_S3_KEY',
  'INVALID_EXTRACTION_ATTEMPT_ID',
  'PAYMENT_PROOF_EXTRACTION_FAILED',
  'PAYMENT_PROOF_EXTRACTION_IN_PROGRESS'
]);

export const HttpResponseAndStatusCodeSchema = z.object({
  response: z.string(),
  http_status_code: z.number()
});

export const ErrorResponseSchema = HttpResponseAndStatusCodeSchema;

export const ExternalManagedErrorResponseSchema =
  HttpResponseAndStatusCodeSchema.extend({
    res_status: ExternalManagedErrorResStatusSchema
  });

export const BankTransferServiceDetailsSchema = z.object({
  id: z.string(),
  instructions: z.string().nullable().optional(),
  account_number: z.string(),
  ifsc_code: z.string(),
  account_name: z.string(),
  bank_name: z.string(),
  branch_name: z.string().nullable().optional()
});

export const QRCodeServiceDetailsSchema = z.object({
  id: z.string(),
  instructions: z.string().nullable().optional(),
  qr_image_s3_key: z.string()
});

export const PaymentLinkServiceDetailsSchema = z.object({
  id: z.string(),
  instructions: z.string().nullable().optional(),
  payment_link_url: z.string()
});

export const UpiServiceDetailsSchema = z.object({
  id: z.string(),
  instructions: z.string().nullable().optional(),
  upi_id: z.string()
});

export const ExternalManagedPaymentServicesDetailsResponseSchema = z.object({
  service_type: z.string(),
  service_reference_id: z.string(),
  service_name: z.string(),
  bank_transfer_service_details:
    BankTransferServiceDetailsSchema.nullable().optional(),
  qr_code_service_details: QRCodeServiceDetailsSchema.nullable().optional(),
  payment_link_service_details:
    PaymentLinkServiceDetailsSchema.nullable().optional(),
  upi_service_details: UpiServiceDetailsSchema.nullable().optional()
});

export const GetExternalManagedPaymentServicesDetailsRequestSchema = z.object({
  service_type: z.string(),
  service_reference_id: z.string()
});

export const CreateUserOrderExternalManagedPaymentsDetailsRequestSchema =
  z.object({
    order_id: z.string(),
    amount_paid_in_least_subunit: z.number(),
    payment_reference_number: z.string(),
    paid_datetime: z.string().datetime(),
    payment_proof_s3_key: z.string()
  });

export const CreateUserOrderExternalManagedPaymentsDetailsResponseSchema =
  z.record(z.unknown());

/** Normalized API values for `payments[].verification_status`. */
export const ExternalManagedPaymentVerificationStatus = {
  PENDING: 'PENDING',
  VERIFIED: 'VERIFIED',
  REJECTED: 'REJECTED'
} as const;

export type ExternalManagedPaymentVerificationStatusValue =
  (typeof ExternalManagedPaymentVerificationStatus)[keyof typeof ExternalManagedPaymentVerificationStatus];

export const normalizeExternalManagedVerificationStatus = (
  status: string | null | undefined
): string => (status ?? '').trim().toUpperCase();

export const isExternalManagedVerificationPending = (
  status: string | null | undefined
): boolean =>
  normalizeExternalManagedVerificationStatus(status) ===
  ExternalManagedPaymentVerificationStatus.PENDING;

export const ExternalManagedPaymentEntrySchema = z.object({
  payment_details_id: z.string(),
  amount_paid_in_least_subunit: z.number(),
  payment_reference_number: z.string(),
  paid_datetime: z.string(),
  payment_proof_s3_key: z.string(),
  verification_status: z.string(),
  verified_datetime: z.string().nullable().optional(),
  verification_remarks: z.string().nullable().optional()
});

export const UserOrderExternalManagedPaymentDetailsResponseSchema = z.object({
  total_order_amount_in_least_subunit: z.number(),
  total_verified_amount_in_least_subunit: z.number(),
  remaining_amount_in_least_subunit: z.number(),
  payments: z.array(ExternalManagedPaymentEntrySchema)
});

export const GetUserOrderExternalManagedPaymentDetailsRequestSchema = z.object({
  order_id: z.string()
});

export const InitiatePaymentProofExtractionRequestSchema = z.object({
  payment_proof_s3_key: z.string()
});

export const InitiatePaymentProofExtractionResponseSchema = z.object({
  extraction_attempt_id: z.string()
});

export const GetPaymentProofExtractionResultRequestSchema = z.object({
  extraction_attempt_id: z.string()
});

export const GetPaymentProofExtractionResultResponseSchema = z.object({
  amount_paid_in_least_subunit: z.number().nullable().optional(),
  payment_reference_number: z.string().nullable().optional(),
  paid_datetime: z.string().nullable().optional()
});

export const normalizeGetPaymentProofExtractionResultResponse = (
  payload: unknown
): z.infer<typeof GetPaymentProofExtractionResultResponseSchema> => {
  const record =
    payload != null && typeof payload === 'object'
      ? (payload as Record<string, unknown>)
      : {};

  return {
    amount_paid_in_least_subunit:
      record.amount_paid_in_least_subunit == null
        ? undefined
        : (record.amount_paid_in_least_subunit as number),
    payment_reference_number:
      record.payment_reference_number == null
        ? undefined
        : String(record.payment_reference_number),
    paid_datetime:
      record.paid_datetime == null ? undefined : String(record.paid_datetime)
  };
};

export type ExternalManagedPaymentServiceType = z.infer<
  typeof ExternalManagedPaymentServiceTypeSchema
>;
export type BankTransferServiceDetailsType = z.infer<
  typeof BankTransferServiceDetailsSchema
>;
export type QRCodeServiceDetailsType = z.infer<
  typeof QRCodeServiceDetailsSchema
>;
export type PaymentLinkServiceDetailsType = z.infer<
  typeof PaymentLinkServiceDetailsSchema
>;
export type UpiServiceDetailsType = z.infer<typeof UpiServiceDetailsSchema>;
export type ExternalManagedPaymentServicesDetailsResponseType = z.infer<
  typeof ExternalManagedPaymentServicesDetailsResponseSchema
>;
export type GetExternalManagedPaymentServicesDetailsRequestType = z.infer<
  typeof GetExternalManagedPaymentServicesDetailsRequestSchema
>;
export type CreateUserOrderExternalManagedPaymentsDetailsRequestType = z.infer<
  typeof CreateUserOrderExternalManagedPaymentsDetailsRequestSchema
>;
export type CreateUserOrderExternalManagedPaymentsDetailsResponseType = z.infer<
  typeof CreateUserOrderExternalManagedPaymentsDetailsResponseSchema
>;
export type UserOrderExternalManagedPaymentDetailsResponseType = z.infer<
  typeof UserOrderExternalManagedPaymentDetailsResponseSchema
>;
export type GetUserOrderExternalManagedPaymentDetailsRequestType = z.infer<
  typeof GetUserOrderExternalManagedPaymentDetailsRequestSchema
>;
export type ExternalManagedErrorResponseType = z.infer<
  typeof ExternalManagedErrorResponseSchema
>;
export type InitiatePaymentProofExtractionRequestType = z.infer<
  typeof InitiatePaymentProofExtractionRequestSchema
>;
export type InitiatePaymentProofExtractionResponseType = z.infer<
  typeof InitiatePaymentProofExtractionResponseSchema
>;
export type GetPaymentProofExtractionResultRequestType = z.infer<
  typeof GetPaymentProofExtractionResultRequestSchema
>;
export type GetPaymentProofExtractionResultResponseType = z.infer<
  typeof GetPaymentProofExtractionResultResponseSchema
>;
